import React, { useEffect, useRef, useState } from 'react';
import GenreFilter from './GenreFilter';
import PlatformFilter from './PlatformFilter';
import RegionCountryFilter from './RegionCountryFilter';
import MetricsFilter from './MetricsFilter';
import useData, { IMetricFilterOption } from './useData';
import { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import CommonFilter from '@/pages/benchmark/components/CommonFilter';
import classNames from 'classnames';
import { Button } from 'tea-component';
import { getName } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import './index.scss';

/**
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

export interface FilterParams {
  genre?: string;
  platform?: string;
  country?: string;
  metrics: RegionSelectValueType;
  gameList?: string[];
}

export interface IBenchmarkFilterProps extends BaseComponentProps {
  // 默认指标筛选器选中的指标
  defaultMetricList: string[];
  // 默认平台
  defaultPlatform: string;
  // 默认国家
  defaultCountry: string;
  // 默认品类
  defaultGenre: string;
  // 默认更多筛选器文本
  moreFilterTextEn?: string;
  moreFilterTextCn?: string;
  // 默认折叠筛选器文本
  collapseFilterTextEn: string;
  collapseFilterTextCn: string;
  // 筛选器默认展示？
  defaultFilterOpen?: boolean;
  // 默认品类
  className?: string;
  [key: string]: any;
}

const inputList: any[] = [];

const BenchmarkFilter = (props: IBenchmarkFilterProps) => {
  const {
    children,
    filterInit,
    filterConfirm,
    filterReset,
    defaultMetricList,
    defaultPlatform,
    defaultCountry = 'global',
    defaultGenre = 'Shooter',
    moreFilterTextEn = 'More Filter',
    moreFilterTextCn = '更多筛选器',
    collapseFilterTextEn = 'Collapse Filter',
    collapseFilterTextCn = '折叠筛选器',
    defaultFilterOpen = false,
    data,
    className,
    style,
    ...resetProps
  } = props;

  // 获取筛选器选项
  const {
    currentType,
    gameOptions,
    genreOptions,
    platformOptions,
    countryOptions,
    metricOptions,
    loading,
    frameType,
    platformMap,
    countryMap,
    isError,
    setCurrentType,
    setGameOptions,
  } = useData(data?.frameType);

  const [isGameError, setIsGameError] = useState(false);

  // 默认值
  const initFilterParams = {
    gameList: [],
    genre: defaultGenre,
    platform: defaultPlatform || frameType,
    country: defaultCountry,
    metrics: {
      type: '',
      regions: [],
      markets: defaultMetricList,
    },
  };

  // 查询参数
  const filterRef = useRef<any>({});

  // 缓存选中指标
  const selectMetricRef = useRef<IMetricFilterOption[]>([]);

  // 缓存选中游戏列表
  const selectGameListRef = useRef<any[]>([]);

  // 缓存初始默认指标
  const initSelectMetricRef = useRef<IMetricFilterOption[]>([]);

  const [filterValue, setFilterValue] =
    useState<FilterParams>(initFilterParams);

  // 指标筛选器选中的指标
  const [selectMetricList, setSelectMetricList] = useState<
    IMetricFilterOption[]
  >([]);

  // 展开收缩
  const [openMoreFilter, setOpenMoreFitler] =
    useState<boolean>(defaultFilterOpen);

  const { isEN, locale } = useLocal();

  // 匹配不到品类，兜底拿第一项
  useEffect(() => {
    if (genreOptions?.length > 0) {
      const genreOpt = genreOptions.find((item) => item.code === defaultGenre);
      if (!genreOpt) {
        setFilterValue({ ...initFilterParams, genre: genreOptions?.[0]?.code });
      }
    }
  }, [genreOptions]);

  // 根据url参数，重新匹配查询项
  useEffect(() => {
    if (!loading) {
      const defaultParams = getFilterValueByUrlParams();
      formatFilterParams(defaultParams);
      // 获取url的指标
      const list =
        metricOptions?.filter((item) =>
          defaultParams.metrics?.markets?.includes(item.metric),
        ) || [];
      selectMetricRef.current = list;
      setSelectMetricList(list);
      setFilterValue(defaultParams);
      // 获取初始化指标
      const initMetricList =
        metricOptions?.filter((item) =>
          initFilterParams.metrics?.markets?.includes(item.metric),
        ) || [];
      initSelectMetricRef.current = initMetricList;
    }
  }, [loading]);

  // 转换接口格式
  const formatFilterParams = (params: FilterParams) => {
    const { platform, country, metrics, genre, gameList } = params;
    filterRef.current = {
      filters: {
        game_list: gameList,
        genre_list: genre && genre !== '255' ? [genre] : [],
        country_list: [country],
        platform_list: [platform],
      },
      frame_type: frameType,
      metrics: metrics?.markets,
    };
  };

  // 获取url上面的查询参数
  const getFilterValueByUrlParams = () => {
    const newParams = new URLSearchParams(window.location.search);
    const genre = newParams.get('genre');
    const platform = newParams.get('platform');
    const country = newParams.get('country');
    const metrics = newParams.get('metrics')?.split(',');
    const isOpenFilter = newParams.get('openMoreFilter');
    selectGameListRef.current = gameOptions; // 缓存游戏

    // 获取折叠状态
    if (isOpenFilter === 'true') {
      setOpenMoreFitler(true);
    } else if (isOpenFilter === 'false') {
      setOpenMoreFitler(false);
    }
    // 首页跳转过来的不处理url
    if (metrics) {
      // 清空地址栏查询参数
      window.history.replaceState(
        { path: window.location.pathname },
        '',
        window.location.pathname,
      );
    }
    const gameIds = gameOptions.map((item) => item.game_id);
    // 特殊处理转255，否则选项选不中全部的值
    let genreValue = genre;
    if (genreValue === '') {
      genreValue = '255';
    }
    return {
      gameList: currentType === 'game' ? gameIds : [],
      genre: currentType === 'genre' ? genreValue || defaultGenre : undefined,
      platform: platform || defaultPlatform || frameType,
      country: country || defaultCountry,
      metrics: {
        type: '',
        regions: [],
        markets: metrics || defaultMetricList,
      },
    };
  };

  // 查询游戏时小于50个游戏浏览器缓存, 超过则消除记录
  const cacheGameList = (options: any[]) => {
    if (currentType === 'game') {
      if (filterValue.gameList?.length <= 50) {
        localStorage.setItem(
          `__dbb_${data?.frameType}_benchmark_filter_game_list__`,
          JSON.stringify(options),
        );
      } else {
        localStorage.removeItem(
          `__dbb_${data?.frameType}_benchmark_filter_game_list__`,
        );
      }
    }
  };

  const handleConfirm = () => {
    if (filterValue.metrics?.markets?.length > 0) {
      // 没有选游戏不允许提交
      if (currentType === 'game' && filterValue.gameList?.length === 0) {
        setIsGameError(true);
        return;
      }
      formatFilterParams(filterValue);
      filterConfirm?.(filterValue);
      setSelectMetricList([...selectMetricRef.current]);
      cacheGameList(selectGameListRef.current);
    } else {
      return false;
    }
  };

  const handleReset = () => {
    formatFilterParams(initFilterParams);
    setCurrentType('genre');
    setGameOptions([]);
    setIsGameError(false);
    filterReset?.(filterValue);
    setFilterValue(initFilterParams);
    setSelectMetricList([...initSelectMetricRef.current]);
    selectMetricRef.current = [...initSelectMetricRef.current];
  };

  const handleInit = () => {
    formatFilterParams(initFilterParams);
    filterInit?.(initFilterParams);
    cacheGameList(gameOptions);
  };

  const changeRegionCountry = (val: string) => {
    setFilterValue({
      ...filterValue,
      country: val,
    });
  };

  const changeGenre = (val: any, type: string) => {
    let gameList: any = [];
    if (type === 'game') {
      gameList = val?.map((item: any) => item.game_id);
      selectGameListRef.current = val;
    }
    setFilterValue({
      ...filterValue,
      genre: type === 'genre' ? val : undefined,
      gameList,
    });
    setCurrentType(type);
  };

  const changePlatform = (val: string) => {
    setFilterValue({
      ...filterValue,
      platform: val,
    });
  };

  const changeMetrics = (
    val: RegionSelectValueType,
    metricList: IMetricFilterOption[],
  ) => {
    setFilterValue({ ...filterValue, metrics: val });
    selectMetricRef.current = metricList;
  };

  return (
    <div
      className={classNames(className, 'dbb-genre-benchmark-filter-contaniner')}
      style={style}
    >
      <CommonFilter
        {...resetProps}
        value={filterRef.current}
        inputList={inputList}
        loadingHeight={159}
        extraRenderContent={
          <div className="dbb-genre-benchmark-filter">
            <GenreFilter
              optionType={currentType}
              gameList={gameOptions}
              options={genreOptions}
              onChange={changeGenre}
              setIsGameError={setIsGameError}
              value={filterValue.genre}
              frameType={frameType}
              isEN={isEN}
              logConfig={resetProps.logConfig}
              isError={isGameError}
            />
            {openMoreFilter && (
              <RegionCountryFilter
                options={countryOptions}
                onChange={changeRegionCountry}
                value={filterValue.country}
                isEN={isEN}
                locale={locale}
              />
            )}
            {openMoreFilter && (
              <PlatformFilter
                options={platformOptions}
                onChange={changePlatform}
                value={filterValue.platform}
                isEN={isEN}
              />
            )}
            <MetricsFilter
              options={metricOptions}
              value={filterValue.metrics}
              otherFilterValue={filterValue}
              onChange={changeMetrics}
              isEN={isEN}
              locale={locale}
              frameType={frameType}
            />
            <div className="dbb-genre-benchmark-more-filter">
              <Button
                type="link"
                onClick={() => setOpenMoreFitler(!openMoreFilter)}
              >
                {openMoreFilter
                  ? getName(isEN, collapseFilterTextEn, collapseFilterTextCn)
                  : getName(isEN, moreFilterTextEn, moreFilterTextCn)}
              </Button>
            </div>
          </div>
        }
        onInit={handleInit}
        onConfirm={handleConfirm}
        onReset={handleReset}
        loading={loading}
        error={isError}
      >
        {React.Children.map(children, (child) =>
          React.cloneElement(child, {
            selectMetricList,
            platformMap,
            countryMap,
            openMoreFilter,
            currentType,
          }),
        )}
      </CommonFilter>
    </div>
  );
};

export default BenchmarkFilter;
